import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  CreateWhiteboard,
  Whiteboard,
  WhiteboardFolder,
} from '../types/whiteboard'
import {
  createWhiteboardFolder,
  loadAllWhiteboardFolders,
  loadWhiteboard,
  loadWhiteboardFolders,
  loadWhiteboards,
  loadWhiteboardsByFolder,
  modifyWhiteboard,
  removeWhiteboardFolder,
  removeWhiteboard,
  saveWhiteboard,
  updateWhiteboardFolder,
} from '../api/whiteboard'

const normalizeRef = (value?: string | null) => {
  if (value === undefined || value === null) return undefined
  const normalized = String(value).trim()
  if (!normalized || normalized === 'undefined' || normalized === 'null') {
    return undefined
  }
  return normalized
}

// get whiteboards
export const getAllWhiteboards = createAsyncThunk(
  'whiteboard/getWhiteboards',
  async (_, thunkApi) => {
    try {
      return await loadWhiteboards()
    } catch (e) {
      return thunkApi.rejectWithValue('getWhiteboards Error')
    }
  },
)

export const getWhiteboardsByFolder = createAsyncThunk(
  'whiteboard/getWhiteboardsByFolder',
  async (folderId: string | undefined, thunkApi) => {
    try {
      return await loadWhiteboardsByFolder(folderId)
    } catch (e) {
      return thunkApi.rejectWithValue('getWhiteboardsByFolder Error')
    }
  },
)

export const getWhiteboardFolders = createAsyncThunk(
  'whiteboard/getWhiteboardFolders',
  async (parentId: string | undefined, thunkApi) => {
    try {
      return await loadWhiteboardFolders(parentId)
    } catch (e) {
      return thunkApi.rejectWithValue('getWhiteboardFolders Error')
    }
  },
)

export const getAllWhiteboardFolders = createAsyncThunk(
  'whiteboard/getAllWhiteboardFolders',
  async (_, thunkApi) => {
    try {
      return await loadAllWhiteboardFolders()
    } catch (e) {
      return thunkApi.rejectWithValue('getAllWhiteboardFolders Error')
    }
  },
)

export const addWhiteboardFolder = createAsyncThunk(
  'whiteboard/addWhiteboardFolder',
  async (
    payload: { name: string; parentId?: string; isPrivate?: boolean },
    thunkApi,
  ) => {
    try {
      return await createWhiteboardFolder(payload)
    } catch (e) {
      return thunkApi.rejectWithValue('addWhiteboardFolder Error')
    }
  },
)

export const updateWhiteboardFolderAction = createAsyncThunk(
  'whiteboard/updateWhiteboardFolder',
  async (
    data: { id: string; updates: Partial<WhiteboardFolder> },
    thunkApi,
  ) => {
    try {
      return await updateWhiteboardFolder(data.id, data.updates)
    } catch (e) {
      return thunkApi.rejectWithValue('updateWhiteboardFolder Error')
    }
  },
)

export const deleteWhiteboardFolderAction = createAsyncThunk(
  'whiteboard/deleteWhiteboardFolder',
  async (id: string, thunkApi) => {
    try {
      return await removeWhiteboardFolder(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteWhiteboardFolder Error')
    }
  },
)

// get whiteboard
export const getWhiteboard = createAsyncThunk(
  'whiteboard/getWhiteboard',
  async (whiteboardId: string, thunkApi) => {
    try {
      return await loadWhiteboard(whiteboardId)
    } catch (e) {
      return thunkApi.rejectWithValue('getWhiteboards Error')
    }
  },
)

// create whiteboard
export const addWhiteboard = createAsyncThunk(
  'whiteboard/addWhiteboard',
  async (whiteboard: CreateWhiteboard, thunkApi) => {
    try {
      return await saveWhiteboard(whiteboard)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// update whiteboard
export const updateWhiteboard = createAsyncThunk(
  'whiteboard/updateWhiteboard',
  async (whiteboard: Whiteboard, thunkApi) => {
    try {
      return await modifyWhiteboard(whiteboard)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// // delete whiteboard
export const deleteWhiteboard = createAsyncThunk(
  'whiteboard/deleteWhiteboard',
  async (id: string, thunkApi) => {
    try {
      return await removeWhiteboard(id)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

interface WhiteboardSliceState {
  // Full list used for global search.
  whiteboards: Whiteboard[]
  // Folder-scoped list used by the /whiteboards page.
  folderWhiteboards: Whiteboard[]
  folders: WhiteboardFolder[]
  allFolders: WhiteboardFolder[]
  activeFolderId?: string
  activeParentId?: string
  whiteboard?: Whiteboard
  loading: boolean
  error?: SerializedError
}
const initialState: WhiteboardSliceState = {
  whiteboards: [],
  folderWhiteboards: [],
  folders: [],
  allFolders: [],
  activeFolderId: undefined,
  activeParentId: undefined,
  whiteboard: undefined,
  loading: false,
  error: undefined,
}

const whiteboardSlice = createSlice({
  name: 'whiteboards',
  initialState,
  reducers: {
    setWhiteboard: (
      state: WhiteboardSliceState,
      action: PayloadAction<Whiteboard | undefined>,
    ) => {
      state.whiteboard = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllWhiteboards.fulfilled, (
      state,
      action: PayloadAction<Whiteboard[]>,
    ) => {
      // @ts-ignore
      state.whiteboards = action.payload?.sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getWhiteboardsByFolder.fulfilled, (
      state,
      action: PayloadAction<Whiteboard[]>,
    ) => {
      state.folderWhiteboards = (action.payload?.sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      ) || []) as any
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getWhiteboardsByFolder.pending, (state, action: any) => {
      state.activeFolderId = action.meta.arg
      state.folderWhiteboards = []
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getWhiteboardsByFolder.rejected, (state, action: any) => {
      if (state.activeFolderId !== action.meta.arg) {
        return
      }

      state.loading = false
      state.error = action.error
    })
    builder.addCase(getWhiteboardFolders.fulfilled, (
      state,
      action: PayloadAction<WhiteboardFolder[]>,
    ) => {
      state.folders = action.payload || []
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getWhiteboardFolders.pending, (state, action: any) => {
      state.activeParentId = action.meta.arg
      state.folders = []
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getWhiteboardFolders.rejected, (state, action: any) => {
      if (state.activeParentId !== action.meta.arg) {
        return
      }

      state.loading = false
      state.error = action.error
    })
    builder.addCase(getAllWhiteboardFolders.fulfilled, (
      state,
      action: PayloadAction<WhiteboardFolder[]>,
    ) => {
      state.allFolders = action.payload || []
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getAllWhiteboardFolders.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getAllWhiteboardFolders.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(addWhiteboardFolder.fulfilled, (
      state,
      action: PayloadAction<WhiteboardFolder>,
    ) => {
      const folder = action.payload
      if (
        normalizeRef(folder.parentId) === normalizeRef(state.activeParentId)
      ) {
        state.folders = [...state.folders, folder]
      }

      state.allFolders = [...state.allFolders, folder]
      state.loading = false
      state.error = undefined
    })
    builder.addCase(addWhiteboardFolder.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addWhiteboardFolder.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(updateWhiteboardFolderAction.fulfilled, (
      state,
      action: PayloadAction<WhiteboardFolder>,
    ) => {
      const updated = action.payload

      state.folders = state.folders
        .filter((folder) => folder.id !== updated.id)
        .concat(
          normalizeRef(updated.parentId) === normalizeRef(state.activeParentId)
            ? [updated]
            : [],
        )

      state.allFolders = state.allFolders.map((folder) =>
        folder.id === updated.id ? updated : folder,
      )

      state.loading = false
      state.error = undefined
    })
    builder.addCase(updateWhiteboardFolderAction.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateWhiteboardFolderAction.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(deleteWhiteboardFolderAction.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.folders = state.folders.filter(
        (folder) => folder.id !== action.payload,
      )
      state.allFolders = state.allFolders.filter(
        (folder) => folder.id !== action.payload,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(deleteWhiteboardFolderAction.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteWhiteboardFolderAction.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(getAllWhiteboards.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getAllWhiteboards.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(getWhiteboard.fulfilled, (
      state,
      action: PayloadAction<Whiteboard>,
    ) => {
      // @ts-ignore
      state.whiteboard = action.payload
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getWhiteboard.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getWhiteboard.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(addWhiteboard.fulfilled, (state, action) => {
      if (!action.payload) {
        state.loading = false
        return
      }

      state.whiteboards.unshift(action.payload as any)

      if (
        normalizeRef(action.payload.folderId) ===
        normalizeRef(state.activeFolderId)
      ) {
        state.folderWhiteboards.unshift(action.payload as any)
      }

      state.loading = false
      state.error = undefined
    })
    builder.addCase(addWhiteboard.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addWhiteboard.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(updateWhiteboard.fulfilled, (
      state,
      action: PayloadAction<Whiteboard>,
    ) => {
      state.whiteboards = state.whiteboards.map((whiteboard) =>
        whiteboard.id === action.payload.id
          ? (action.payload as any)
          : whiteboard,
      )

      const inActiveFolder =
        normalizeRef(action.payload.folderId) ===
        normalizeRef(state.activeFolderId)

      state.folderWhiteboards = state.folderWhiteboards.filter(
        (whiteboard) => whiteboard.id !== action.payload.id,
      )

      if (inActiveFolder) {
        state.folderWhiteboards.unshift(action.payload as any)
      }

      state.loading = false
      state.error = undefined
    })
    builder.addCase(updateWhiteboard.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateWhiteboard.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(deleteWhiteboard.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.whiteboards = state.whiteboards.filter(
        (whiteboard) => whiteboard.id !== action.payload,
      )
      state.folderWhiteboards = state.folderWhiteboards.filter(
        (whiteboard) => whiteboard.id !== action.payload,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(deleteWhiteboard.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteWhiteboard.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
  },
})

export const { setWhiteboard } = whiteboardSlice.actions
export default whiteboardSlice.reducer
